import { PageBar, type PageBarRemove } from "@/components/page-bar";
import type { BreadcrumbCrumb } from "@/components/breadcrumbs";
import type { ActionMenuItem } from "@/components/ui/action-menu";

export function PageHeader({
  eyebrow, title, subtitle, actions, breadcrumb, breadcrumbAside, breadcrumbMenu, remove,
}: {
  eyebrow?: string;
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  /**
   * Breadcrumb control. Three states:
   *   - `BreadcrumbCrumb[]` — render this explicit, return-aware trail
   *     (detail pages: `Inbox › BIZ-12 › Patch v3`).
   *   - `undefined` — derive the trail from the route automatically.
   *     Sub/detail pages get clickable section roots with the title as
   *     the leaf; top-level list pages fall back to the plain `eyebrow`.
   *   - `false` — suppress the bar (the page renders its own).
   */
  breadcrumb?: BreadcrumbCrumb[] | false;
  /**
   * Rendered at the right edge of the breadcrumb row. Navigation chrome that
   * is about WHERE you are (a list pager, a record switcher) belongs here,
   * not among the action buttons — mixed in with them it competes with the
   * page's primary action for the same attention.
   */
  breadcrumbAside?: React.ReactNode;
  /**
   * Record-level overflow items for the breadcrumb row's `⋯` menu — the
   * things you do to the record itself (export it, archive it) rather than
   * the things you do inside it, which stay in `actions`.
   */
  breadcrumbMenu?: ActionMenuItem[];
  /**
   * Standard delete for the record this page shows: adds a confirmed
   * "Delete …" entry to the breadcrumb row's `⋯` menu. See `<PageBar>` —
   * it owns the confirm, the call, the failure toast and the redirect.
   */
  remove?: PageBarRemove;
}) {
  // A Fragment, deliberately: `position: sticky` is clipped to the element's
  // CONTAINING BLOCK, so a wrapper <div> around the bar and the title would
  // pin the bar to a ~100px box and it would scroll away with the header.
  // As a direct child of the page container the bar stays put for the whole
  // page, and the container's own `space-y-*` provides the gap below it.
  return (
    <>
      {breadcrumb !== false && (
        <PageBar
          breadcrumb={Array.isArray(breadcrumb) ? breadcrumb : undefined}
          leaf={title}
          eyebrow={eyebrow}
          aside={breadcrumbAside}
          menu={breadcrumbMenu}
          remove={remove}
        />
      )}
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight text-fg-bright">{title}</h1>
          {/* The subtitle's LINE is reserved whenever a page declares one,
              even while the value behind it is still null.
              This is the single biggest source of layout shift the app had:
              a detail page renders its header immediately and its subtitle a
              fetch later ("Support desk · Ready"), and the 22px that line
              occupies used to appear out of nowhere and push the entire page
              — rail, content, everything — down. Measured at CLS 0.167 on
              desktop and 0.190 on an iPad in landscape, on every project
              page, which was most of the app's total.
              `subtitle !== undefined` is the test, not `subtitle` being
              truthy: a page that never has a subtitle reserves nothing, and
              a page whose subtitle resolves late reserves it from the first
              frame. */}
          {subtitle !== undefined && (
            <div className="text-[13px] leading-[1.35] text-fg-dim mt-1 min-h-[1.35em]">
              {subtitle}
            </div>
          )}
        </div>
        {/* Actions only. The per-page "Ask the Command Center" button used to
            sit here, but the top bar carries a permanent Command Center button
            (and ⌘⇧K) on every page, so it was a second affordance for the same
            panel in the same viewport — noise beside the page's real action.

            Same reservation, same reason: the primary action is resolved
            server-side on some pages and a fetch later on others, and on a
            narrow viewport (the header stacks below `sm`) a button arriving
            into a zero-height box moved everything under it. 30px is the
            height of the small buttons this slot actually holds. */}
        {actions !== undefined && (
          <div className="flex items-center gap-2 flex-wrap min-h-[30px] sm:shrink-0">
            {actions}
          </div>
        )}
      </header>
    </>
  );
}
